import React, { useState, useEffect } from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Button,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Avatar,
  Divider,
  Alert,
} from '@mantine/core';
import { IconCheck, IconAlertCircle, IconEdit } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';
import { User, Team, TeamApiData, CreateTeamData, Department, TeamRole } from '../../types';
import { TeamRolePicker } from './TeamRolePicker';
import { suggestTeamRole, teamRoleName } from '../../utils/teamRole';
import { useTeamRoles } from '../../hooks/useReferenceApi';
import { usePermission } from '../../hooks/usePermission';

interface TeamModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (teamData: CreateTeamData) => void;
  loading?: boolean;
  team?: Team; // Если передан, то режим редактирования
}

export const TeamModal: React.FC<TeamModalProps> = ({
  opened,
  onClose,
  onSubmit,
  loading = false,
  team,
}) => {
  const { t } = useTranslation();
  const isEditMode = !!team;
  // Лид, PM, состав, роли — и у команды с людьми тип, статус, отдел — рёбра руководства: от них
  // зависит, кто читает данные людей и пишет их саммари. Без people.manage сервер их не меняет
  // (403), поэтому поля сразу неактивны. У пустой команды тип, статус и отдел — структура.
  const canManagePeople = usePermission('people.manage');
  const hasPeople = !!(team?.lead || team?.pm || team?.members?.length);
  const arePeopleLocked = !canManagePeople;
  const isSetupLocked = !canManagePeople && hasPeople;
  // Подсказка — по разу на блок (тип, лид, участники), а не под каждым неактивным полем.
  const queryClient = useQueryClient();

  // Получение пользователей с бэкенда
  const { data: users = [], isLoading: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => api('/users'),
    staleTime: 5 * 60 * 1000,
    enabled: opened,
  });

  // Получение департаментов с бэкенда
  const { data: departments = [], isLoading: isDepartmentsLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => api('/departments'),
    staleTime: 5 * 60 * 1000,
    enabled: opened,
  });

  // Роли участников — справочник компании.
  const { data: teamRoles = [] } = useTeamRoles();

  // Мутация создания/редактирования команды
  const teamMutation = useMutation({
    mutationFn: async (teamData: TeamApiData) => {
      if (isEditMode && teamData._id) {
        return api(`/teams/${teamData._id}`, {
          method: 'PATCH',
          body: teamData,
        });
      } else {
        return api('/teams', {
          method: 'POST',
          body: teamData,
        });
      }
    },
    onSuccess: () => {
      showToast({
        title: isEditMode
          ? t('messages.updated', { item: t('teams.title') })
          : t('messages.created', { item: t('teams.title') }),
        message: '',
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      onClose();
    },
    onError: (error) => {
      const message = getErrorMessage(
        error,
        isEditMode
          ? t('messages.errorUpdating', { item: t('teams.title') })
          : t('messages.errorCreating', { item: t('teams.title') }),
      );
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const [formData, setFormData] = useState<TeamApiData>({
    name: '',
    description: '',
    department: '',
    lead: '',
    pm: '',
    members: [],
    kind: 'product',
    status: 'active',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Заполняем форму данными команды при открытии модала в режиме редактирования
  useEffect(() => {
    if (team && opened && isEditMode) {
      setFormData({
        _id: team._id,
        name: team.name,
        description: team.description || '',
        department: team.department?._id || '',
        lead: team.lead?._id || '',
        pm: team.pm?._id || '',
        // Участник без user — удалённый пользователь: раньше на нём падал весь
        // экран. Лид и PM выкидываются здесь же: в списке выбора их нет, и
        // Mantine показал бы такое значение сырым id, который не убрать.
        members: (team.members || [])
          .filter(
            (member) =>
              member.user?._id &&
              member.user._id !== team.lead?._id &&
              member.user._id !== team.pm?._id,
          )
          .map((member) => ({ user: member.user._id, role: member.role })),
        kind: team.kind || 'product',
        status: team.status,
      });
      setErrors({});
    } else if (!isEditMode && opened) {
      // Сбрасываем форму для создания новой команды
      setFormData({
        name: '',
        description: '',
        department: '',
        lead: '',
        pm: '',
        members: [],
        kind: 'product',
        status: 'active',
      });
      setErrors({});
    }
  }, [team, opened, isEditMode]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('teams.validation.nameRequired');
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t('teams.validation.nameMin');
    }

    // description is optional (matches backend) — no validation

    if (!formData.department) {
      newErrors.department = t('teams.validation.departmentRequired');
    }

    // Без права назначать людей лид не выбирается — и не требуется.
    if (!formData.lead && !arePeopleLocked) {
      newErrors.lead = t('teams.validation.leadRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      teamMutation.mutate(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      department: '',
      lead: '',
      pm: '',
      members: [],
      kind: 'product',
    });
    setErrors({});
    onClose();
  };

  const memberIds = (formData.members || []).map((m) => m.user);
  // Уволенных (архив) в выборе нет; кто уже в команде — остаётся, иначе сохранение его выкинет.
  const chosen = new Set([formData.lead, formData.pm, ...memberIds].filter(Boolean));
  const pickable = users.filter((u: User) => u.active !== false || chosen.has(u._id));
  const userById = (id: string): User | undefined => users.find((u: User) => u._id === id);

  // Выбор людей и выбор ролей разведены: MultiSelect отдаёт только id, роль
  // подставляется по профессии и дальше правится в карточке участника.
  const setMemberIds = (ids: string[]) => {
    const previous = new Map((formData.members || []).map((m) => [m.user, m.role]));
    setFormData({
      ...formData,
      members: ids.map((id) => ({
        user: id,
        role: previous.get(id) ?? suggestTeamRole(userById(id), teamRoles),
      })),
    });
  };

  const setMemberRole = (id: string, role: TeamRole) => {
    setFormData({
      ...formData,
      members: (formData.members || []).map((m) => (m.user === id ? { ...m, role } : m)),
    });
  };

  const isFunctional = formData.kind === 'functional';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? t('teams.editTeam') : t('teams.addTeam')}
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="lg">
          {/* Основная информация */}
          <div>
            <Divider label={t('teams.basicInfo')} labelPosition="left" mb="md" />

            <Stack gap="md">
              <TextInput
                label={t('teams.name')}
                placeholder={t('teams.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={errors.name}
                required
              />

              <Textarea
                label={t('teams.description')}
                placeholder={t('teams.description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                minRows={3}
              />

              <Select
                label={t('teams.kind')}
                description={isSetupLocked ? t('teams.peopleLockedHint') : t('teams.kindHint')}
                disabled={isSetupLocked}
                value={formData.kind || 'product'}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    kind: (value as 'product' | 'functional') || 'product',
                    // У функциональной группы PM не бывает — сбрасываем, чтобы
                    // скрытое поле не уехало на сервер.
                    pm: value === 'functional' ? '' : formData.pm,
                  })
                }
                data={[
                  { value: 'product', label: t('teams.kindProduct') },
                  { value: 'functional', label: t('teams.kindFunctional') },
                ]}
                allowDeselect={false}
              />

              <Select
                label={t('common.status')}
                disabled={isSetupLocked}
                value={formData.status || 'active'}
                onChange={(value) =>
                  setFormData({ ...formData, status: value === 'inactive' ? 'inactive' : 'active' })
                }
                data={[
                  { value: 'active', label: t('teams.active') },
                  { value: 'inactive', label: t('teams.inactive') },
                ]}
                allowDeselect={false}
              />

              <Select
                label={t('teams.department')}
                placeholder={t('teams.selectDepartment')}
                value={formData.department}
                onChange={(value) => setFormData({ ...formData, department: value || '' })}
                data={departments.map((dept: Department) => ({
                  value: dept._id,
                  label: dept.name,
                }))}
                error={errors.department}
                disabled={isDepartmentsLoading || isSetupLocked}
                required
              />
            </Stack>
          </div>

          {/* Лид и PM команды — один и тот же пикер, разная роль */}
          <div>
            <Divider label={t('teams.leadership')} labelPosition="left" mb="md" />

            <TeamRolePicker
              label={isFunctional ? t('teams.groupHead') : t('teams.selectTeamLead')}
              description={arePeopleLocked ? t('teams.peopleLockedHint') : undefined}
              placeholder={t('teams.selectUser')}
              users={pickable}
              value={formData.lead || ''}
              onChange={(userId) =>
                // Лид не может одновременно числиться участником команды.
                setFormData({
                  ...formData,
                  lead: userId,
                  members: userId
                    ? (formData.members || []).filter((m) => m.user !== userId)
                    : formData.members || [],
                })
              }
              badgeLabel={isFunctional ? t('teams.groupHead') : t('teams.teamLead')}
              badgeColor="teal"
              required={!arePeopleLocked}
              error={errors.lead}
              disabled={isUsersLoading || arePeopleLocked}
            />

            {!isFunctional && (
              <TeamRolePicker
                label={t('teams.selectPm')}
                description={t('teams.pmHint')}
                placeholder={t('teams.selectUser')}
                users={pickable}
                value={formData.pm || ''}
                onChange={(userId) =>
                  // PM, как и лид, не должен попадать в участники команды.
                  setFormData({
                    ...formData,
                    pm: userId,
                    members: userId
                      ? (formData.members || []).filter((m) => m.user !== userId)
                      : formData.members || [],
                  })
                }
                badgeLabel={t('teams.pmRole')}
                badgeColor="grape"
                clearable
                disabled={isUsersLoading || arePeopleLocked}
                mt="md"
              />
            )}
          </div>

          {/* Участники команды */}
          <div>
            <Divider
              label={
                memberIds.length > 0
                  ? `${t('teams.members')} · ${memberIds.length}`
                  : t('teams.members')
              }
              labelPosition="left"
              mb="md"
            />

            <MultiSelect
              label={t('teams.selectMembers')}
              description={arePeopleLocked ? t('teams.peopleLockedHint') : undefined}
              placeholder={t('teams.selectUsers')}
              value={memberIds}
              onChange={(value) => setMemberIds(value || [])}
              // В чипе — только имя: с почтой десять выбранных занимали больше
              // места, чем список под полем, где те же люди показаны заново.
              // Почта остаётся в подсказке выпадающего списка — там она и нужна,
              // чтобы различить тёзок.
              data={pickable
                .filter((user: User) => user._id !== formData.lead && user._id !== formData.pm)
                .map((user: User) => ({ value: user._id, label: user.name }))}
              renderOption={({ option }) => (
                <div>
                  <Text size="sm">{option.label}</Text>
                  <Text size="xs" c="dimmed">
                    {userById(option.value)?.email}
                  </Text>
                </div>
              )}
              disabled={isUsersLoading || arePeopleLocked}
              searchable
              clearable
              maxDropdownHeight={200}
            />

            {memberIds.length > 0 && (
              <Stack gap={2} mt="md">
                {(formData.members || []).map((member) => {
                  const user = userById(member.user);
                  return (
                    <Group key={member.user} gap="sm" wrap="nowrap" py={4}>
                      <Avatar src={user?.avatar} radius="xl" size={34}>
                        {user?.name?.slice(0, 1)}
                      </Avatar>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate>
                          {user?.name}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {user?.position || user?.email}
                        </Text>
                      </div>
                      <Select
                        aria-label={t('teams.role')}
                        size="xs"
                        disabled={arePeopleLocked}
                        value={member.role}
                        onChange={(value) => value && setMemberRole(member.user, value as TeamRole)}
                        data={[
                          ...teamRoles.map((role) => ({
                            value: role.key,
                            label: teamRoleName(teamRoles, role.key, t),
                          })),
                          // Роль, которой уже нет в справочнике, всё равно видна у участника.
                          ...(teamRoles.some((r) => r.key === member.role)
                            ? []
                            : [
                                {
                                  value: member.role,
                                  label: teamRoleName(teamRoles, member.role, t),
                                },
                              ]),
                        ]}
                        allowDeselect={false}
                        w={150}
                      />
                    </Group>
                  );
                })}
              </Stack>
            )}
          </div>

          {/* Сообщения об ошибках */}
          {Object.keys(errors).length > 0 && (
            <Alert icon={<IconAlertCircle size={16} />} title={t('common.error')} color="red">
              <Stack gap={4}>
                {Object.values(errors).map((msg, i) => (
                  <Text key={i} size="sm">
                    {msg}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          {/* Кнопки */}
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={handleClose} color="gray">
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={loading || teamMutation.isPending}
              leftSection={
                !loading && (isEditMode ? <IconEdit size={16} /> : <IconCheck size={16} />)
              }
              color="teal"
            >
              {loading
                ? isEditMode
                  ? t('common.saving')
                  : t('common.creating')
                : isEditMode
                  ? t('common.save')
                  : t('common.add')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
